import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import React from "react";
import styled, { ThemeConsumer } from "styled-components";

export type XcPanelRowPresentationProps = {
    /** The background color of the panel.
     * @themable */
    color?: string;
    /** Props to be passed into the inner Icon component.
     * @themable */
    moreIconProps?: IconPresentationProps;
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp; // using this construction instead of InjectableCss to avoid deep type instantiation
    /** CSS string or styled-components function to be injected into row representing current location.
     * @themable */
    isCurrentCss?: StyledProp; // using this construction instead of InjectableCss to avoid deep type instantiation
};

type XcPanelRowComponentProps = ClickableProps & {
    /** Flag governing whether this row is a header row, which governs padding. */
    header?: boolean;
    /** Flag governing whether this row represents a menu option with children, in which case a chevron will be included. */
    hasChildren?: boolean;
    /** Is this the current page? */
    isCurrent?: boolean;
};

// In the below type, MobiusStyledComponentProps is provided by `ClickableProps`
export type XcPanelRowProps = XcPanelRowPresentationProps & XcPanelRowComponentProps;

const XcPanelRowStyle = styled(Clickable)<any>`
    margin-bottom: 0;
    margin-top: 0;
    display: flex;
    width: 100%;
    padding: 0;
    justify-content: space-between;
    background: ${({ theme, color }) => resolveColor(color, theme)};
    color: ${({ theme, color }) => getContrastColor(color, theme)};
    ${injectCss}    
`;

/**
 * A building block for the `PanelMenu` and surrounding UIs.
 */
const XcPanelRow: React.FC<XcPanelRowProps> = ({ children, theme, ...otherProps }) => (
    <ThemeConsumer>
        {(theme?: BaseTheme) => {
            const { spreadProps } = applyPropBuilder(
                { panelRowProps: { ...otherProps }, theme },
                { component: "panelMenu" },
            );
            const panelRowThemeProps = spreadProps("panelRowProps");
            const appliedColor = panelRowThemeProps.color ?? "common.accent";
            return (
                <XcPanelRowStyle {...panelRowThemeProps} {...otherProps} color={appliedColor}>
                    {children}
                </XcPanelRowStyle>
            );
        }}
    </ThemeConsumer>
);

/** @ignore */
export default XcPanelRow;
